import type { AxiosError } from "axios";
import useSWR, { type SWRResponse } from "swr";
import type { CountryType, Nullable } from "../../../@types/generic";
import { numberToString } from "../../../helpers/formatters/values";
import { useCountry } from "../../../hooks/useCountry";
import { QUERY_NAME_LIVECAM_BY_ID } from "../../cacheKeys";
import { fetcher, isAxiosError } from "../index";
import type { Livecam } from "../types/livecam";

const fetchLivecam = async (
    id: string,
    country: CountryType
): Promise<Nullable<Livecam>> =>
    fetcher<Livecam>({ path: `/livecams/${id}/` }, { country })
        .then(({ data }) => data)
        .catch((error: Error | AxiosError) => {
            if (isAxiosError(error) && error.status === 404) {
                return null;
            }

            return Promise.reject(error);
        });

export const useLivecam = (
    id: Nullable<string>
): SWRResponse<Nullable<Livecam>> => {
    const country = useCountry();
    const { data, isLoading, ...rest } = useSWR<Nullable<Livecam>>(
        numberToString(id) !== ""
            ? [QUERY_NAME_LIVECAM_BY_ID, id, country]
            : null,
        () => fetchLivecam(id as string, country)
    );

    return {
        data,
        isLoading: isLoading || data === undefined,
        ...rest,
    };
};
